<template>
  <div class="user-app-chrome__top-spacer" aria-hidden="true"></div>

  <Teleport to="body">
    <div class="user-app-chrome">
      <header class="user-app-chrome__topbar" aria-label="Navigazione area utente">
        <button type="button" class="user-app-chrome__action" aria-label="Torna indietro" @click="goBack">
          <span class="material-icons" aria-hidden="true">arrow_back</span>
        </button>

        <div class="user-app-chrome__brand" aria-label="Fusco Fit">
          <img :src="logoUrl" alt="Fusco Fit" />
        </div>

        <button type="button" class="user-app-chrome__action" aria-label="Profilo" @click="goToProfile">
          <span class="material-icons" aria-hidden="true">person_outline</span>
        </button>
      </header>

      <nav v-if="showDock" ref="dockElement" class="user-app-chrome__dock" aria-label="Navigazione rapida">
        <button
          type="button"
          class="user-app-chrome__dock-item"
          :class="{ 'user-app-chrome__dock-item--active': activeDockItem === 'home' }"
          :aria-current="activeDockItem === 'home' ? 'page' : undefined"
          aria-label="Home"
          @click="goToDashboardSection('dashboard-home')"
        >
          <span class="material-icons" aria-hidden="true">home</span>
          <span class="user-app-chrome__dock-label" aria-hidden="true">Home</span>
          <span class="user-app-chrome__dock-label-short" aria-hidden="true">Home</span>
        </button>
        <button
          type="button"
          class="user-app-chrome__dock-item"
          :class="{ 'user-app-chrome__dock-item--active': activeDockItem === 'support' }"
          :aria-current="activeDockItem === 'support' ? 'location' : undefined"
          aria-label="Assistenza"
          @click="goToDashboardSection('dashboard-support')"
        >
          <span class="material-icons" aria-hidden="true">support_agent</span>
          <span class="user-app-chrome__dock-label" aria-hidden="true">Assistenza</span>
          <span class="user-app-chrome__dock-label-short" aria-hidden="true">Assis.</span>
        </button>
        <button
          type="button"
          class="user-app-chrome__dock-item"
          :class="{ 'user-app-chrome__dock-item--active': activeDockItem === 'paths' }"
          :aria-current="activeDockItem === 'paths' ? 'page' : undefined"
          aria-label="Percorsi"
          @click="goToPage(PROGRAMS_PATH)"
        >
          <span class="material-icons" aria-hidden="true">route</span>
          <span class="user-app-chrome__dock-label" aria-hidden="true">Percorsi</span>
          <span class="user-app-chrome__dock-label-short" aria-hidden="true">Perc.</span>
        </button>
        <button
          type="button"
          class="user-app-chrome__dock-item"
          :class="{ 'user-app-chrome__dock-item--active': activeDockItem === 'nutrition' }"
          :aria-current="activeDockItem === 'nutrition' ? 'page' : undefined"
          aria-label="Alimentazioni"
          @click="goToPage(ALIMENTATION_PATH)"
        >
          <span class="material-icons" aria-hidden="true">restaurant_menu</span>
          <span class="user-app-chrome__dock-label" aria-hidden="true">Alimentazioni</span>
          <span class="user-app-chrome__dock-label-short" aria-hidden="true">Alim.</span>
        </button>
        <button
          type="button"
          class="user-app-chrome__dock-item"
          :class="{ 'user-app-chrome__dock-item--active': activeDockItem === 'training' }"
          :aria-current="activeDockItem === 'training' ? 'page' : undefined"
          aria-label="Allenamenti"
          @click="goToPage(TRAINING_PATH)"
        >
          <span class="material-icons" aria-hidden="true">fitness_center</span>
          <span class="user-app-chrome__dock-label" aria-hidden="true">Allenamenti</span>
          <span class="user-app-chrome__dock-label-short" aria-hidden="true">Allen.</span>
        </button>
      </nav>
    </div>
  </Teleport>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import logoUrl from '../../../fuscofit/assets/logo-black.png';

const DASHBOARD_PATH = '/user';
const PROFILE_PATH = '/user-anagrafica';
const PROGRAMS_PATH = '/user-programmi';
const ALIMENTATION_PATH = '/alimentazione-page';
const TRAINING_PATH = '/i-miei-allenamenti';
const SECTION_QUERY = {
  'dashboard-support': 'support',
  'dashboard-path': 'path',
};

export default {
  name: 'UserAppChrome',
  props: {
    backMode: {
      type: String,
      default: 'dashboard',
      validator: value => ['dashboard', 'history'].includes(value),
    },
    showDock: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['section'],
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const dashboardSection = ref('home');
    const dockElement = ref(null);
    let dockResizeObserver;

    const updateDockHeight = () => {
      if (!dockElement.value) return;
      const height = Math.ceil(dockElement.value.getBoundingClientRect().height);
      document.body.style.setProperty('--user-app-dock-height', `${height}px`);
    };

    onMounted(async () => {
      document.body.classList.add('user-app-active');
      await nextTick();

      updateDockHeight();
      if (dockElement.value && typeof ResizeObserver !== 'undefined') {
        dockResizeObserver = new ResizeObserver(updateDockHeight);
        dockResizeObserver.observe(dockElement.value);
      }

      // Ogni schermata utente parte dal proprio inizio. Lo scroll e su #app,
      // non piu sul body, per lasciare top bar e dock realmente fisse.
      const appContainers = document.querySelectorAll('#app');
      appContainers[appContainers.length - 1]?.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });

    onBeforeUnmount(() => {
      dockResizeObserver?.disconnect();
      document.body.style.removeProperty('--user-app-dock-height');
      document.body.classList.remove('user-app-active');
    });

    const activeDockItem = computed(() => {
      if (route.path === '/come-funziona') return 'home';
      if (route.path === ALIMENTATION_PATH || route.path === '/integrazione') return 'nutrition';
      if (route.path === TRAINING_PATH) return 'training';
      if (route.path === PROGRAMS_PATH || route.path === '/check-storico') return 'paths';
      if (
        route.path === '/ticket-page'
        || route.path === '/ticket-page-tecnico'
        || route.path === '/create-ticket'
        || route.path === '/create-ticket-tecnico'
        || route.path.startsWith('/user/conversation')
      ) return 'support';
      if (route.path !== DASHBOARD_PATH) return '';

      if (route.query.section === 'support') return 'support';
      if (route.query.section === 'path') return 'paths';
      return dashboardSection.value;
    });

    const goBack = () => {
      if (props.backMode === 'history' && window.history.length > 1) {
        router.back();
        return;
      }
      router.push(DASHBOARD_PATH);
    };

    const goToProfile = () => {
      if (route.path !== PROFILE_PATH) router.push(PROFILE_PATH);
    };

    const goToDashboardSection = sectionId => {
      dashboardSection.value = sectionId === 'dashboard-support'
        ? 'support'
        : sectionId === 'dashboard-path'
          ? 'paths'
          : 'home';

      const section = SECTION_QUERY[sectionId];

      if (route.path === DASHBOARD_PATH) {
        const currentSection = typeof route.query.section === 'string' ? route.query.section : '';
        if (currentSection !== (section || '')) {
          router.replace({
            path: DASHBOARD_PATH,
            query: section ? { section } : {},
          });
        }
        emit('section', sectionId);
        return;
      }

      router.push({
        path: DASHBOARD_PATH,
        query: section ? { section } : {},
      });
    };

    const goToPage = path => {
      if (route.path !== path) router.push(path);
    };

    return {
      ALIMENTATION_PATH,
      PROGRAMS_PATH,
      TRAINING_PATH,
      activeDockItem,
      dockElement,
      logoUrl,
      goBack,
      goToDashboardSection,
      goToPage,
      goToProfile,
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;600;700&display=swap');

.user-app-chrome__top-spacer {
  height: calc(56px + env(safe-area-inset-top, 0px));
}

.user-app-chrome,
.user-app-chrome *,
.user-app-chrome *::before,
.user-app-chrome *::after {
  box-sizing: border-box;
}

.user-app-chrome {
  --chrome-black: #111110;
  --chrome-white: #ffffff;
  --chrome-red: #c8102e;
  font-family: "DM Sans", sans-serif;
}

.user-app-chrome__topbar {
  position: fixed;
  z-index: 1000;
  top: calc(6px + env(safe-area-inset-top, 0px));
  left: 50%;
  display: grid;
  grid-template-columns: 32px 1fr 32px;
  align-items: center;
  gap: 10px;
  width: min(calc(100% - 48px), 1392px);
  min-height: 50px;
  padding: 0 10px;
  border: 1px solid rgba(255, 255, 255, 0.68);
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.68), rgba(255, 255, 255, 0.3));
  box-shadow: 0 10px 30px rgba(17, 17, 16, 0.09), inset 0 1px 0 rgba(255, 255, 255, 0.72);
  transform: translateX(-50%);
  backdrop-filter: blur(26px) saturate(1.7);
  -webkit-backdrop-filter: blur(26px) saturate(1.7);
}

.user-app-chrome__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  min-width: 32px;
  min-height: 32px;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.66);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  color: var(--chrome-black);
  box-shadow: none;
  cursor: pointer;
  appearance: none;
  transition: color 0.2s, border-color 0.2s, background 0.2s, transform 0.2s;
}

.user-app-chrome__action:hover {
  border-color: var(--chrome-red);
  background: var(--chrome-red);
  color: var(--chrome-white);
  transform: translateY(-1px);
}

.user-app-chrome__action:focus-visible,
.user-app-chrome__dock-item:focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.3);
  outline-offset: 3px;
}

.user-app-chrome__action .material-icons {
  color: currentColor;
  font-size: 17px;
  line-height: 1;
}

.user-app-chrome__brand {
  justify-self: center;
  display: flex;
  align-items: center;
  width: 74px;
  height: 27px;
}

.user-app-chrome__brand img {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.user-app-chrome__dock {
  position: fixed;
  z-index: 1000;
  bottom: max(8px, env(safe-area-inset-bottom, 0px));
  left: 50%;
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  width: min(calc(100% - 32px), 560px);
  min-height: 64px;
  padding: 7px;
  border: 1px solid rgba(255, 255, 255, 0.24);
  border-radius: 22px;
  background: linear-gradient(135deg, rgba(28, 28, 27, 0.78), rgba(17, 17, 16, 0.58));
  box-shadow:
    0 22px 54px rgba(17, 17, 16, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.2),
    inset 0 -1px 0 rgba(255, 255, 255, 0.05);
  transform: translateX(-50%);
  backdrop-filter: blur(30px) saturate(1.65);
  -webkit-backdrop-filter: blur(30px) saturate(1.65);
}

.user-app-chrome__dock::before {
  content: "";
  position: absolute;
  top: 0;
  right: 12%;
  left: 12%;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.7), transparent);
  opacity: 0.7;
  pointer-events: none;
}

.user-app-chrome__dock-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 0;
  min-height: 50px;
  margin: 0;
  padding: 5px 4px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
  box-shadow: none;
  font: inherit;
  font-size: var(--ff-type-label);
  font-weight: 700;
  letter-spacing: 0.02em;
  cursor: pointer;
  overflow: hidden;
  appearance: none;
  transition: color 0.2s, background 0.2s, transform 0.2s;
}

.user-app-chrome__dock-item > :is(.user-app-chrome__dock-label, .user-app-chrome__dock-label-short) {
  display: block;
  width: 100%;
  min-width: 0;
  line-height: 1.1;
  text-align: center;
  white-space: nowrap;
}

.user-app-chrome__dock-item > .user-app-chrome__dock-label-short {
  display: none;
}

.user-app-chrome__dock-item .material-icons {
  color: currentColor;
  font-size: 21px;
  line-height: 1;
}

.user-app-chrome__dock-item:hover,
.user-app-chrome__dock-item:focus-visible {
  background: rgba(255, 255, 255, 0.1);
  color: var(--chrome-white);
}

.user-app-chrome__dock-item:active {
  background: rgba(200, 16, 46, 0.84);
  color: var(--chrome-white);
  transform: scale(0.94);
}

.user-app-chrome__dock-item.user-app-chrome__dock-item--active,
.user-app-chrome__dock-item.user-app-chrome__dock-item--active:hover,
.user-app-chrome__dock-item.user-app-chrome__dock-item--active:focus-visible {
  background: rgba(200, 16, 46, 0.88);
  color: var(--chrome-white);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 8px 20px rgba(200, 16, 46, 0.25);
}

@media (max-width: 700px) {
  .user-app-chrome__top-spacer {
    height: calc(50px + env(safe-area-inset-top, 0px));
  }

  .user-app-chrome__topbar {
    grid-template-columns: 30px 1fr 30px;
    width: calc(100% - 14px);
    min-height: 44px;
    padding: 0 7px;
    border-radius: 14px;
  }

  .user-app-chrome__action {
    width: 30px;
    height: 30px;
    min-width: 30px;
    min-height: 30px;
  }

  .user-app-chrome__action .material-icons {
    font-size: 16px;
  }

  .user-app-chrome__brand {
    width: 66px;
    height: 24px;
  }

  .user-app-chrome__dock {
    width: calc(100% - 28px);
    min-height: 68px;
    padding: 6px;
    border-radius: 20px;
  }

  .user-app-chrome__dock-item {
    min-height: 56px;
    padding: 5px 1px;
    border-radius: 14px;
    font-size: clamp(8px, 2.45vw, 11px);
    letter-spacing: -0.02em;
  }

  .user-app-chrome__dock-item > :is(.user-app-chrome__dock-label, .user-app-chrome__dock-label-short) {
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .user-app-chrome__dock-item .material-icons {
    font-size: 20px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .user-app-chrome__action,
  .user-app-chrome__dock-item {
    transition: none;
  }
}

@media (max-width: 300px) {
  .user-app-chrome__dock-item > .user-app-chrome__dock-label {
    display: none;
  }

  .user-app-chrome__dock-item > .user-app-chrome__dock-label-short {
    display: block;
    white-space: nowrap;
    overflow-wrap: normal;
  }

  .user-app-chrome__dock-item {
    font-size: 9px;
    letter-spacing: 0;
  }
}
</style>
